import EventStatsTable, {
  type EventStatsTableRowData,
  GetEventStatsTable,
} from '-/Event/EventStats/EventStatsTable';
import { useLocalSearchParams } from 'expo-router';
import { useRef, useState } from 'react';
import { ScrollView } from 'react-native';
import { useQuery } from 'urql';
import PaginatorIndeterminate from '@/components/Custom/PaginatorIndeterminate';
import type { TableNextProps } from '@/components/Custom/TableNext.types';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import WidgetPoweredBy from '@/components/Widget/WidgetPoweredBy';
import { graphql } from '@/gql/graphql';
import usePaginatedList from '@/hooks/usePaginatedList';

const PAGE_LIMIT = 25;

const GetEventStatsWidget = graphql(
  /* GraphQL */ `
    query GetEventStatsWidget($eventId: uuid!) {
      event_by_pk(id: $eventId) {
        title
      }
    }
  `,
);

export default function EventStatsWidget() {
  const [sortColumn, setSortColumn] = useState<
    TableNextProps<EventStatsTableRowData>['sortColumn'] | null
  >(null);

  const { eventId = '' } = useLocalSearchParams<{
    eventId: string;
  }>();

  const scrollViewRef = useRef<ScrollView | null>(null);

  const paginated = usePaginatedList({
    limit: PAGE_LIMIT,
    scrollViewRef,
    indeterminate: true,
  });

  const [{ data }] = useQuery({
    query: GetEventStatsWidget,
    variables: {
      eventId,
    },
    pause: !eventId,
  });

  const [{ data: tableData, fetching: tableFetching }] = useQuery({
    query: GetEventStatsTable,
    variables: {
      eventId,
      groupBy: 'personId',
      rankBy: sortColumn?.id,
      rankDirection: sortColumn?.direction,
      limit: paginated.limit,
      offset: paginated.offset,
    },
  });

  return (
    <Box direction="vertical" flex>
      <Box py="xl" borderBottomWidth={2} border="divider">
        <Text bold numberOfLines={1} size="2xl" center>
          {data?.event_by_pk?.title}
        </Text>
      </Box>
      <ScrollView ref={scrollViewRef} contentContainerStyle={{ flexGrow: 1 }}>
        <EventStatsTable
          data={tableData}
          fetching={tableFetching}
          sortColumn={sortColumn}
          setSortColumn={setSortColumn}
        />
        <PaginatorIndeterminate
          {...paginated}
          total={tableData?.badge_widget_values?.length}
        />
      </ScrollView>
      <WidgetPoweredBy />
    </Box>
  );
}
